// src/pages/Subscription.tsx
import { useState, useEffect } from 'react';
import { 
  X, Check, Loader2, Tag, Crown, Sparkles, Zap, 
  Clock, Shield, Camera, Brain, RefreshCw, AlertCircle,
  ChevronRight, Star
} from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import LongevityBot from '../components/LongevityBot';

interface SubscriptionProps {
  onBack: () => void;
}

export default function Subscription({ onBack }: SubscriptionProps) {
  const {
    plans,
    subscriptionStatus,
    isPro,
    isTrialActive,
    isExpired,
    aiScansRemaining,
    daysUntilExpiry,
    dailyUsage,
    createCheckoutSession,
    isLoading: contextLoading,
  } = useSubscription();

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Calculate savings percentages based on lowest-duration plan
  const getSavingsPercent = (plan: typeof plans[0]) => {
    if (plans.length === 0) return 0;
    const shortestPlan = plans.reduce((min, p) => p.duration_months < min.duration_months ? p : min);
    if (shortestPlan.id === plan.id) return 0;
    const shortestMonthly = shortestPlan.price / shortestPlan.duration_months;
    const thisMonthly = plan.price / plan.duration_months;
    return Math.round((1 - thisMonthly / shortestMonthly) * 100);
  };

  // Determine which plan is "popular" (usually 6-month)
  const isPopularPlan = (plan: typeof plans[0]) => {
    return plan.duration_months === 6;
  };

  useEffect(() => {
    // Default to the 6-month plan (or first plan)
    const popularPlan = plans.find(p => isPopularPlan(p));
    if (popularPlan) {
      setSelectedPlan(popularPlan.id);
    } else if (plans.length > 0) {
      setSelectedPlan(plans[0].id);
    }
  }, [plans]);

  const handleSubscribe = async () => {
    if (!selectedPlan) return;

    setIsProcessing(true);
    setError('');

    try {
      const result = await createCheckoutSession(selectedPlan);
      
      if (result?.checkout_url) {
        // Redirect to Dodo checkout
        window.location.href = result.checkout_url;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = () => {
    if (isPro) {
      return (
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full text-white text-xs font-bold">
          <Crown size={12} />
          PRO MEMBER
        </div>
      );
    }
    if (isTrialActive) {
      return (
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-100 text-cyan-700 rounded-full text-xs font-bold">
          <Clock size={12} />
          TRIAL: {daysUntilExpiry} days left
        </div>
      );
    }
    if (isExpired) {
      return (
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">
          <AlertCircle size={12} />
          TRIAL EXPIRED
        </div>
      );
    }
    return null;
  };

  const formatPrice = (price: number) => {
    // Your schema stores price in dollars, not cents
    return price.toFixed(2);
  };

  const getMonthlyRate = (plan: typeof plans[0]) => {
    // Your schema stores price in dollars
    return (plan.price / plan.duration_months).toFixed(2);
  };

  if (contextLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-400 via-sky-300 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-400 via-sky-300 to-sky-100 pb-24 relative overflow-hidden">
      {/* Background light rays effect */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-80 pointer-events-none">
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full"
          style={{
            background: `
              linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.1) 100%),
              repeating-conic-gradient(from 0deg at 50% 0%, transparent 0deg, rgba(255,255,255,0.03) 2deg, transparent 4deg)
            `
          }}
        />
      </div>

      <div className="max-w-2xl mx-auto relative">
        {/* Close Button */}
        <button
          onClick={onBack}
          className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all backdrop-blur-sm"
        >
          <X size={24} className="text-white" />
        </button>

        {/* Mascot Section */}
        <div className="flex justify-center pt-8 pb-4 relative z-10">
          <LongevityBot size={140} mode="payment" />
        </div>

        {/* Status Badge */}
        <div className="text-center px-6 pb-2">
          {getStatusBadge()}
        </div>

        {/* Header */}
        <div className="text-center px-6 pb-6 relative z-10">
          <h1 className="text-3xl font-black text-white mb-1 drop-shadow-sm">
            {isPro ? 'Manage Your Subscription' : 'Unlock Full Access'}
          </h1>
          <p className="text-white/90 font-medium">
            {isPro 
              ? `Your subscription ends in ${daysUntilExpiry} days`
              : isExpired 
                ? 'Your trial has expired. Upgrade to continue!'
                : 'Try free for 7 days, then upgrade'
            }
          </p>
        </div>

        {/* White Content Card */}
        <div className="bg-white rounded-t-[32px] min-h-screen relative z-0 shadow-2xl">
          <div className="px-5 pt-6 pb-6 space-y-4">
            
            {/* Current Usage Section */}
            {!isPro && (
              <div className="bg-gradient-to-br from-sky-50 to-cyan-50 rounded-2xl p-5 border border-sky-200">
                <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Clock size={16} className="text-sky-600" />
                  Today's Usage
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-xl p-3 border border-sky-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Camera size={14} className="text-sky-600" />
                      <span className="text-xs text-gray-600">AI Scans</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      {dailyUsage.ai_scans_count}
                      <span className="text-gray-400 text-sm font-normal">
                        /{subscriptionStatus?.daily_ai_limit || 4}
                      </span>
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-sky-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Brain size={14} className="text-purple-600" />
                      <span className="text-xs text-gray-600">Protocol Gen</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      {subscriptionStatus?.can_generate_protocol ? '1 left' : '0 left'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Features Comparison */}
            <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3">What you get with Pro</h3>
              
              {/* Feature List */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Camera size={12} className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">8 AI Scans per day</p>
                    <p className="text-xs text-gray-500">Free tier: 4 scans/day</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <RefreshCw size={12} className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Unlimited Protocol Regeneration</p>
                    <p className="text-xs text-gray-500">Free tier: 1 protocol only. Pro: regenerate every 5 days</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={12} className="text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Unlimited manual food logging</p>
                    <p className="text-xs text-gray-500">Available in both tiers</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles size={12} className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Full feature access</p>
                    <p className="text-xs text-gray-500">Future face, advanced analytics & more</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X size={12} className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">No ads, ever</p>
                    <p className="text-xs text-gray-500">Clean, distraction-free experience</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Cards */}
            {!isPro && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-gray-800 px-1">Choose your plan</h3>
                
                {plans.map((plan) => {
                  const isSelected = selectedPlan === plan.id;
                  
                  return (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`relative w-full rounded-2xl p-4 border-2 transition-all text-left ${
                        isSelected
                          ? 'border-sky-500 bg-sky-50/50 shadow-lg'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      {/* Popular Badge */}
                      {isPopularPlan(plan) && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md whitespace-nowrap flex items-center gap-1">
                            <Star size={10} fill="white" />
                            MOST POPULAR
                          </span>
                        </div>
                      )}

                      {/* Savings Badge */}
                      {getSavingsPercent(plan) > 0 && !isPopularPlan(plan) && (
                        <div className="absolute -top-3 right-4">
                          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-[10px] font-bold">
                            SAVE {getSavingsPercent(plan)}%
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        {/* Plan Details */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className={`text-lg font-bold ${isSelected ? 'text-sky-600' : 'text-gray-900'}`}>
                              {plan.name}
                            </h3>
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-sky-500 flex items-center justify-center">
                                <Check size={12} className="text-white" strokeWidth={3} />
                              </div>
                            )}
                          </div>
                          <p className={`text-sm ${isSelected ? 'text-sky-500' : 'text-gray-500'}`}>
                            Billed once • {plan.duration_months} months access
                          </p>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            ${getMonthlyRate(plan)}/mo
                          </p>
                          <p className={`text-sm ${isSelected ? 'text-sky-600' : 'text-gray-500'}`}>
                            ${formatPrice(plan.price)} total
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">{error}</p>
                  <button 
                    onClick={() => setError('')}
                    className="text-xs text-red-600 hover:text-red-800 mt-1"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Security Note */}
            <div className="flex items-center justify-center gap-2 text-gray-400 text-xs py-2">
              <Shield size={14} />
              <span>Secure payment powered by Dodo Payments</span>
            </div>

            {/* Terms */}
            <p className="text-center text-xs text-gray-500">
              By subscribing, you agree to our Terms of Service and Privacy Policy.
              <br />
              One-time payment. No auto-renewal.
            </p>
          </div>
        </div>

        {/* Fixed Bottom CTA */}
        {!isPro && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 z-50">
            <div className="max-w-2xl mx-auto">
              <button
                onClick={handleSubscribe}
                disabled={!selectedPlan || isProcessing}
                className="w-full py-4 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 disabled:from-gray-300 disabled:to-gray-400 text-white font-bold text-lg rounded-2xl transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap size={20} />
                    Get Pro Access
                    <ChevronRight size={20} />
                  </>
                )}
              </button>
              
              {isTrialActive && (
                <p className="text-center text-xs text-gray-500 mt-2">
                  Your trial continues until you purchase
                </p>
              )}
            </div>
          </div>
        )}

        {/* Pro Member Footer */}
        {isPro && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 z-50">
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-sm text-gray-600 mb-2">
                Your Pro subscription is active
              </p>
              <p className="text-xs text-gray-400">
                Expires in {daysUntilExpiry} days • Need help? Contact support
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}